import * as React from 'react'

import { RowData, Table } from '@tanstack/react-table'

import { DEFAULT_COLUMN_MIN_SIZE, UNCAPPED_COLUMN_SIZE } from './columns'
import {
  distributeColumnWidths,
  FillColumnSpec,
} from './distributeColumnWidths'

const DEFAULT_COLUMN_SIZE = 150
const EMPTY_WIDTHS: Record<string, number> = {}

interface FillContainerWidthOptions {
  enabled: boolean
  columnWidths?: Record<string, number>
  // The shared clamped write path for deliberate widths; the end-of-drag fold
  // persists through it so intent isn't written two different ways.
  setColumnWidth?: (columnId: string, width: number) => void
}

// Distributes the table container's surplus width across its columns by writing
// real pixel sizes into TanStack's columnSizing, so getSize/getTotalSize/getStart
// (and therefore sticky offsets, band rows and virtualization) stay truthful.
// Each column grows from its size (floor) up to its maxSize (ceiling), so a
// column with maxSize === size never grows. Columns present in columnWidths are
// the user's deliberate widths: frozen out of the flex, contributing their
// (clamped) width to the total.
export default function useFillContainerWidth<TData extends RowData>(
  table: Table<TData>,
  scrollElementRef: React.RefObject<HTMLElement | null>,
  { enabled, columnWidths, setColumnWidth }: FillContainerWidthOptions,
): void {
  const [containerWidth, setContainerWidth] = React.useState(0)
  const lastResizingIdRef = React.useRef<string | null>(null)
  const widths = columnWidths ?? EMPTY_WIDTHS

  React.useLayoutEffect(() => {
    const el = scrollElementRef.current
    if (!enabled || !el || typeof ResizeObserver === 'undefined') {
      return
    }

    const observer = new ResizeObserver(entries => {
      setContainerWidth(
        Math.floor(entries[0]?.contentRect.width ?? el.clientWidth),
      )
    })
    observer.observe(el)
    setContainerWidth(Math.floor(el.clientWidth))

    return () => observer.disconnect()
  }, [enabled, scrollElementRef])

  const { columnSizing, columnSizingInfo } = table.getState()
  const resizingColumnId = columnSizingInfo.isResizingColumn

  // Remember the column under an active resize so its final width can be folded
  // into intent once the drag ends (below). Fires on resize start/stop only.
  React.useEffect(() => {
    if (resizingColumnId) {
      lastResizingIdRef.current = resizingColumnId
    }
  }, [resizingColumnId])

  // Re-run when the visible column SET changes (show/hide/reorder); columnSizing
  // alone won't, since a hidden column keeps its stale entry.
  const layoutKey = table
    .getVisibleLeafColumns()
    .map(column => column.id)
    .join('|')

  React.useLayoutEffect(() => {
    if (!enabled || !containerWidth || resizingColumnId) {
      return
    }

    const columns = table.getVisibleLeafColumns()

    // A just-ended drag's final width is sitting in columnSizing; fold it into
    // intent once so the column stays frozen (it is then excluded from the flex
    // below). Reading at the commit boundary, in this effect, avoids a race with
    // the derive overwriting it.
    let effectiveWidths = widths
    const endedId = lastResizingIdRef.current
    if (endedId) {
      lastResizingIdRef.current = null
      const dragged = columnSizing[endedId]
      if (dragged != null && dragged !== widths[endedId]) {
        effectiveWidths = { ...widths, [endedId]: dragged }
        setColumnWidth?.(endedId, dragged)
      }
    }

    const specs: FillColumnSpec[] = columns.map(column => ({
      id: column.id,
      base: column.columnDef.size ?? DEFAULT_COLUMN_SIZE,
      min: column.columnDef.minSize ?? DEFAULT_COLUMN_MIN_SIZE,
      max: column.columnDef.maxSize ?? UNCAPPED_COLUMN_SIZE,
      override: effectiveWidths[column.id],
    }))

    const next: Record<string, number> = {
      ...columnSizing,
      ...distributeColumnWidths(specs, containerWidth),
    }

    const changed = columns.some(
      column => next[column.id] !== columnSizing[column.id],
    )
    if (!changed) {
      return
    }

    table.setColumnSizing(next)
  }, [
    enabled,
    containerWidth,
    layoutKey,
    columnSizing,
    columnWidths,
    resizingColumnId,
    setColumnWidth,
    table,
  ])
}
